import React, { useEffect, useRef } from 'react';

import { Navigate, Outlet } from 'react-router-dom';

import Loader from 'components/Loader/index';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import useAppNavigate from 'hooks/useAppNavigate';
import { AppRoutes } from 'prequalificationV2/routes/routes.enums';
import { getAccount, getBorrowerByToken } from 'prequalificationV2/thunks';
import { getApplicationInfoForDashboardThunk } from 'prequalificationV2/thunks/getApplicationInfoForDashboard';
import { setAccount, setPreQualificationStep } from 'prequalificationV2/actions';
import { resetAllSteps } from 'prequalificationV2/actions/stages';
import { setPrequalificationPortal } from 'prequalificationV2/actions/prequalificationPortal';
import { getPostLoginNavigation } from 'prequalificationV2/pages/PreQualification/prequalification.utils';
import { NextStepDestination } from 'prequalificationV2/utils/nextStepNavigation';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import { applicationApi } from 'prequalificationV2/api/ApplicationApi';
import { marketingApi } from 'api/MarketingApi';
import { IS_CONCEPTUAL_HQ_ENABLED } from 'constants/constants';
import { PrequalificationVersion } from 'enums/ApplicationsVariables';
import { FeatureFlag } from 'enums/featureFlag.enums';
import { getConceptualDeviceId } from 'prequalificationV2/routes/routes.utils';

const PrivateRoute = () => {
  const navigator = useAppNavigate();
  const [isLoading, setIsLoading] = React.useState(true);
  const isMounted = useRef(true);
  const refreshToken = localStorage.getItem('refreshToken');
  const dispatch = useAppDispatch();
  const {
    borrower: { borrowerId },
  } = useAppSelector((state) => state);

  const updateDeviceId = async () => {
    if (IS_CONCEPTUAL_HQ_ENABLED) {
      const deviceId = await getConceptualDeviceId();

      if (deviceId) {
        marketingApi.updateDevice({ deviceId: deviceId });
      }
    }
  };

  const getInitialData = async () => {
    try {
      if (!refreshToken) {
        return;
      }

      const account = await dispatch(getAccount()).unwrap();

      const borrower = await dispatch(getBorrowerByToken()).unwrap();

      dispatch(setAccount({ email: account.email, phone: account.phones[0]?.value, id: borrower.id }));

      const application = await dispatch(getApplicationInfoForDashboardThunk(borrower.id!)).unwrap();
      // Thunk returns an empty array when there's no active application or on error
      const borrowerApplication = 'id' in application ? application : undefined;

      if (borrowerApplication?.id && borrowerApplication?.prequalificationVersion !== PrequalificationVersion.Second) {
        localStorage.setItem(FeatureFlag.IsNewPrequalificationEnabled, 'false');
        window.location.reload();

        return;
      }

      if (borrowerApplication?.id) {
        const navigation = await getPostLoginNavigation();

        if (navigation.destination === NextStepDestination.Prequalification && navigation.prequalificationStep) {
          dispatch(setPrequalificationPortal({ isPrequalStepPreset: true }));
          dispatch(setPreQualificationStep(navigation.prequalificationStep));
          navigator(AppRoutes.Prequalification);

          return;
        }

        applicationApi.updateLoanPortalAccessTime(borrowerApplication.id);
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    dispatch(resetAllSteps());
    getInitialData();
    updateDeviceId();

    // we need login User only once when page is loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => {
      isMounted.current = false;
    };
  }, []);

  if (isLoading) {
    return <Loader />;
  }

  if (!refreshToken || !borrowerId) {
    return <Navigate to={AppRoutes.Login} />;
  }

  return <Outlet />;
};

export default PrivateRoute;
